import { mutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ApiEndpoint } from '@/constants';
import {
    useUIStore, useUserStore,
} from '@/stores';
import {
    LoginPayload, LoginResponse, Objection, ObjectionCreatePayload, ObjectionUpdatePayload, Project, ProjectCreatePayload, ProjectUpdatePayload, ReorderPayload, Script, ScriptComment, ScriptCopyPayload, ScriptCreatePayload, ScriptUpdatePayload, Setting, SettingCreatePayload, SettingUpdatePayload, SupportTicket, SupportTicketCreatePayload, SupportTicketUpdatePayload, User, UserCreatePayload, UserUpdatePayload,
} from '@/types';
import {
    api, queryClient,
} from '@/utils';

export const authMutations = {
    login: () =>
        mutationOptions<LoginResponse, Error, LoginPayload>({
            mutationKey: ['auth', 'auth:login'],
            mutationFn: async (payload: LoginPayload) => {
                const response = await api.post(ApiEndpoint.Login, payload);
                return response.data;
            },
            onSuccess: (response) => {
                useUserStore.getState().setUser(response.data);
            },
            onError: (error) => {
                let message = 'Не вдалося ввійти в систему';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    logout: () =>
        mutationOptions<void, Error>({
            mutationKey: ['auth', 'auth:logout'],
            mutationFn: async () => {
                await api.post(ApiEndpoint.Logout);
            },
        }),
};

export const objectionsMutations = {
    delete: (id: string) =>
        mutationOptions<void, AxiosError>({
            mutationKey: ['objections', 'objections:delete', id],
            mutationFn: async () => {
                await api.delete(`${ApiEndpoint.Objections}/${id}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['objections'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Заперечення видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити заперечення';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    create: () =>
        mutationOptions<Objection, AxiosError, ObjectionCreatePayload>({
            mutationKey: ['objections', 'objections:create'],
            mutationFn: async (payload: ObjectionCreatePayload) => {
                const response = await api.post(ApiEndpoint.Objections, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['objections'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Заперечення створено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося створити заперечення';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    update: (id: string) =>
        mutationOptions<Objection, AxiosError, ObjectionUpdatePayload>({
            mutationKey: ['objections', 'objections:update', id],
            mutationFn: async (payload: ObjectionUpdatePayload) => {
                const response = await api.put(`${ApiEndpoint.Objections}/${id}`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['objections'] });
                queryClient.invalidateQueries({ queryKey: ['objections', 'objections:one', id] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Заперечення оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити заперечення';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),
};

export const projectsMutations = {
    create: () =>
        mutationOptions<Project, AxiosError, ProjectCreatePayload>({
            mutationKey: ['projects', 'projects:create'],
            mutationFn: async (data: ProjectCreatePayload) => {
                const response = await api.post(ApiEndpoint.Projects, data);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Проект створено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося створити проект';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    update: (id: string) =>
        mutationOptions<void, AxiosError, ProjectUpdatePayload>({
            mutationKey: ['projects', 'projects:update', id],
            mutationFn: async (data: ProjectUpdatePayload) => {
                await api.put(`${ApiEndpoint.Projects}/${id}`, data);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', id] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Проект оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити проект';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    delete: (id: string) =>
        mutationOptions<void, AxiosError>({
            mutationKey: ['projects', 'projects:delete', id],
            mutationFn: async () => {
                await api.delete(`${ApiEndpoint.Projects}/${id}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Проект видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити проект';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    toggleActive: (id: string) =>
        mutationOptions<Project, AxiosError>({
            mutationKey: ['projects', 'projects:toggle-active', id],
            mutationFn: async () => {
                const response = await api.post(`${ApiEndpoint.Projects}/${id}/toggle-active`);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', id] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Статус проекту оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося змінити статус проекту';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    reorder: () =>
        mutationOptions<void, AxiosError, ReorderPayload>({
            mutationKey: ['projects', 'projects:reorder'],
            mutationFn: async (payload: ReorderPayload) => {
                await api.post(ApiEndpoint.ProjectsReorder, payload);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Порядок проектів збережено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося зберегти порядок проектів';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    uploadWysiwygImage: (projectId: string) =>
        mutationOptions<{ url: string; full_url: string }, AxiosError, File>({
            mutationKey: ['projects', 'projects:upload-wysiwyg-image', projectId],
            mutationFn: async (image: File) => {
                const formData = new FormData();
                formData.append('image', image);

                const response = await api.post(
                    `${ApiEndpoint.Projects}/${projectId}/wysiwyg-image`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                );

                return response.data.data;
            },
            onError: () => {
                useUIStore.getState().setNotification({
                    type: 'error',
                    message: 'Не вдалося завантажити зображення',
                });
            },
        }),
};

export const usersMutations = {
    delete: (id: string) =>
        mutationOptions<void, AxiosError>({
            mutationKey: ['users', 'users:delete', id],
            mutationFn: async () => {
                await api.delete(`${ApiEndpoint.Users}/${id}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Користувача видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити користувача';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    create: () =>
        mutationOptions<User, AxiosError, UserCreatePayload>({
            mutationKey: ['users', 'users:create'],
            mutationFn: async (payload: UserCreatePayload) => {
                const response = await api.post(ApiEndpoint.Users, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Користувача створено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося створити користувача';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    update: (id: string) =>
        mutationOptions<User, AxiosError, UserUpdatePayload>({
            mutationKey: ['users', 'users:update', id],
            mutationFn: async (payload: UserUpdatePayload) => {
                const response = await api.put(`${ApiEndpoint.Users}/${id}`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] });
                queryClient.invalidateQueries({ queryKey: ['users', 'users:one', id] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Користувача оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити користувача';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    syncAccess: (id: string) =>
        mutationOptions<User, AxiosError, { project_ids: string[] }>({
            mutationKey: ['users', 'users:sync-access', id],
            mutationFn: async (payload: { project_ids: string[]; }) => {
                const response = await api.post(`${ApiEndpoint.Users}/${id}/sync-access`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] });
                queryClient.invalidateQueries({ queryKey: ['users', 'users:one', id] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Доступи користувача оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити доступи користувача';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),
};

export const scriptsMutations = {
    create: (projectId: string) =>
        mutationOptions<Script, AxiosError, ScriptCreatePayload>({
            mutationKey: ['scripts', 'scripts:create', projectId],
            mutationFn: async (payload: ScriptCreatePayload) => {
                const formData = new FormData();
                formData.append('title', payload.title);

                formData.append('description', payload.description || '');

                formData.append('comment', '');

                formData.append('product_price', payload.product_price || '');
                formData.append('product_sku', payload.product_sku || '');

                formData.append('product_id', payload.product_id || '');

                formData.append('type', payload.type || 'default');

                if (payload.is_active !== undefined) {
                    formData.append('is_active', payload.is_active ? '1' : '0');
                }

                if (payload.instructions && payload.instructions.length > 0) {
                    payload.instructions.forEach((instruction, index) => {
                        formData.append(`instructions[${index}][name]`, instruction.name);
                        formData.append(`instructions[${index}][value]`, instruction.value);
                    });
                }

                if (payload.main_image) {
                    formData.append('main_image', payload.main_image);
                }

                if (payload.gallery && payload.gallery.length > 0) {
                    payload.gallery.forEach((file) => {
                        formData.append('gallery[]', file);
                    });
                }
                const response = await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Скрипт створено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося створити скрипт';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    update: (projectId: string, id: string) =>
        mutationOptions<Script, AxiosError, ScriptUpdatePayload>({
            mutationKey: ['scripts', 'scripts:update', projectId, id],
            mutationFn: async (payload: ScriptUpdatePayload) => {
                const formData = new FormData();
                formData.append('title', payload.title);

                formData.append('description', payload.description || '');

                formData.append('comment', '');

                formData.append('product_price', payload.product_price || '');
                formData.append('product_sku', payload.product_sku || '');

                formData.append('product_id', payload.product_id || '');

                formData.append('type', payload.type || 'default');

                if (payload.is_active !== undefined) {
                    formData.append('is_active', payload.is_active ? '1' : '0');
                }

                if (payload.instructions && payload.instructions.length > 0) {
                    payload.instructions.forEach((instruction, index) => {
                        formData.append(`instructions[${index}][name]`, instruction.name);
                        formData.append(`instructions[${index}][value]`, instruction.value);
                    });
                } else {
                    formData.append('instructions_cleared', '1');
                }

                if (payload.main_image) {
                    formData.append('main_image', payload.main_image);
                }

                if (payload.gallery && payload.gallery.length > 0) {
                    payload.gallery.forEach((file) => {
                        formData.append('gallery[]', file);
                    });
                }

                if (payload.remove_main_image) {
                    formData.append('remove_main_image', '1');
                }

                formData.append('_method', 'PUT');
                const response = await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/${id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['scripts', 'scripts:one', id, projectId] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Скрипт оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити скрипт';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    toggleActive: (projectId: string, scriptId: string) =>
        mutationOptions<Script, AxiosError, void>({
            mutationKey: ['scripts', 'scripts:toggle-active', projectId, scriptId],
            mutationFn: async () => {
                const response = await api.post(
                    `${ApiEndpoint.Projects}/${projectId}/scripts/${scriptId}/toggle-active`,
                );
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['scripts', 'scripts:one', scriptId, projectId] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Статус активності скрипта оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося змінити статус скрипта';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    copy: () =>
        mutationOptions<Script, AxiosError, ScriptCopyPayload>({
            mutationKey: ['scripts', 'scripts:copy'],
            mutationFn: async (payload: ScriptCopyPayload) => {
                const response = await api.post(ApiEndpoint.ScriptsCopy, payload);
                return response.data.data;
            },
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                if (data.project_id) {
                    queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', data.project_id] });
                }
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Скрипт скопійовано',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося скопіювати скрипт';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    delete: (projectId: string, id: string) =>
        mutationOptions<void, AxiosError>({
            mutationKey: ['scripts', 'scripts:delete', projectId, id],
            mutationFn: async () => {
                await api.delete(`${ApiEndpoint.Projects}/${projectId}/scripts/${id}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Скрипт видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити скрипт';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    reorder: (projectId: string) =>
        mutationOptions<void, AxiosError, ReorderPayload>({
            mutationKey: ['scripts', 'scripts:reorder', projectId],
            mutationFn: async (payload: ReorderPayload) => {
                await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/reorder`, payload);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Порядок скриптів збережено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося зберегти порядок скриптів';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    attachCrossSell: (projectId: string, scriptId: string) =>
        mutationOptions<Script, AxiosError, { cross_sell_ids: string[]; sort_orders?: Record<string, number> }>({
            mutationKey: ['scripts', 'scripts:attach-cross-sell', projectId, scriptId],
            mutationFn: async (payload) => {
                const response = await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/${scriptId}/attach-cross-sale`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts', 'scripts:one', scriptId, projectId] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Крос-сейли додано',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося додати крос-сейли';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    detachCrossSell: (projectId: string, scriptId: string) =>
        mutationOptions<Script, AxiosError, { cross_sell_ids: string[] }>({
            mutationKey: ['scripts', 'scripts:detach-cross-sell', projectId, scriptId],
            mutationFn: async (payload) => {
                const response = await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/${scriptId}/detach-cross-sale`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts', 'scripts:one', scriptId, projectId] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Крос-сейли видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити крос-сейли';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    bulkUpdateInstruction: (projectId: string) =>
        mutationOptions<{ message: string }, AxiosError, { instruction_name: string; instruction_value: string }>({
            mutationKey: ['scripts', 'scripts:bulk-update-instruction', projectId],
            mutationFn: async (payload) => {
                const response = await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/bulk-update-instruction`, payload);
                return response.data;
            },
            onError: (error) => {
                let message = 'Не вдалося оновити розділ у всіх скриптах';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    bulkAttachCrossSell: (projectId: string) =>
        mutationOptions<void, AxiosError, { cross_sell_ids: string[] }>({
            mutationKey: ['scripts', 'scripts:bulk-attach-cross-sell', projectId],
            mutationFn: async (payload) => {
                await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/bulk-attach-cross-sale`, payload);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
            },
            onError: (error) => {
                let message = 'Не вдалося додати крос-сейли до всіх скриптів';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    bulkDetachCrossSell: (projectId: string) =>
        mutationOptions<void, AxiosError, { cross_sell_ids: string[] }>({
            mutationKey: ['scripts', 'scripts:bulk-detach-cross-sell', projectId],
            mutationFn: async (payload) => {
                await api.post(`${ApiEndpoint.Projects}/${projectId}/scripts/bulk-detach-cross-sale`, payload);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts'] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити крос-сейли з усіх скриптів';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    deleteMedia: (projectId: string, scriptId: string) =>
        mutationOptions<void, AxiosError, string>({
            mutationKey: ['scripts', 'scripts:delete-media', projectId, scriptId],
            mutationFn: async (mediaId: string) => {
                await api.delete(`${ApiEndpoint.ScriptMedia}/${projectId}/scripts/${scriptId}/media/${mediaId}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts', 'scripts:one', scriptId, projectId] });
                queryClient.invalidateQueries({ queryKey: ['projects', 'projects:one', projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Зображення видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити зображення';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    setMainImage: (projectId: string, scriptId: string) =>
        mutationOptions<void, AxiosError, string>({
            mutationKey: ['scripts', 'scripts:set-main-image', projectId, scriptId],
            mutationFn: async (mediaId: string) => {
                await api.post(`${ApiEndpoint.ScriptMedia}/${projectId}/scripts/${scriptId}/media/${mediaId}/set-main`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['scripts', 'scripts:one', scriptId, projectId] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Головне зображення встановлено',
                });
            },
            onError: () => {
                useUIStore.getState().setNotification({
                    type: 'error',
                    message: 'Не вдалося встановити головне зображення',
                });
            },
        }),

    uploadInstructionImage: (projectId: string, scriptId: string) =>
        mutationOptions<{ url: string; full_url: string }, AxiosError, File>({
            mutationKey: ['scripts', 'scripts:upload-instruction-image', projectId, scriptId],
            mutationFn: async (image: File) => {
                const formData = new FormData();
                formData.append('image', image);

                const response = await api.post(
                    `${ApiEndpoint.ScriptMedia}/${projectId}/scripts/${scriptId}/instruction-image`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    },
                );

                return response.data.data;
            },
            onError: (error) => {
                let message = 'Не вдалося завантажити зображення в інструкцію';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),
};

export const managerMutations = {
    acknowledgeProjectChanges: (projectId: string) =>
        mutationOptions<{ result: boolean; data: { acknowledged_count: number; message: string } }, AxiosError>({
            mutationKey: ['manager-projects', 'acknowledge-changes', projectId],
            mutationFn: async () => {
                const response = await api.post(`${ApiEndpoint.ManagerProjects}/${projectId}/acknowledge-changes`);
                return response.data;
            },
            onSuccess: (response) => {
                queryClient.invalidateQueries({ queryKey: ['manager-projects'] });
                queryClient.invalidateQueries({ queryKey: ['manager-projects', 'manager-projects:one', projectId] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: response.data?.message || 'Зміни підтверджено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося підтвердити зміни';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    acknowledgeScriptChanges: (scriptId: string) =>
        mutationOptions<{ result: boolean; acknowledged_count: number; message: string }, AxiosError>({
            mutationKey: ['manager-scripts', 'acknowledge-changes', scriptId],
            mutationFn: async () => {
                const response = await api.post(`${ApiEndpoint.ManagerScripts}/${scriptId}/acknowledge-changes`);
                return response.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['manager-projects', 'manager-projects:script', scriptId] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Зміни підтверджено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося підтвердити зміни';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),
};

export const supportTicketsMutations = {
    create: () =>
        mutationOptions<SupportTicket, AxiosError, SupportTicketCreatePayload>({
            mutationKey: ['support-tickets', 'support-tickets:create'],
            mutationFn: async (payload: SupportTicketCreatePayload) => {
                const response = await api.post(ApiEndpoint.DashboardSupportTicket, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Запит на підтримку створено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося створити запит на підтримку';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    update: (id: string) =>
        mutationOptions<SupportTicket, AxiosError, SupportTicketUpdatePayload>({
            mutationKey: ['support-tickets', 'support-tickets:update', id],
            mutationFn: async (payload: SupportTicketUpdatePayload) => {
                const response = await api.put(`${ApiEndpoint.SupportTickets}/${id}`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
                queryClient.invalidateQueries({ queryKey: ['support-tickets', 'support-tickets:one', id] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Запит на підтримку оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити запит на підтримку';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    delete: (id: string) =>
        mutationOptions<void, AxiosError>({
            mutationKey: ['support-tickets', 'support-tickets:delete', id],
            mutationFn: async () => {
                await api.delete(`${ApiEndpoint.SupportTickets}/${id}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Запит на підтримку видалено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити запит на підтримку';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),
};

export const scriptCommentsMutations = {
    create: (scriptId: string) =>
        mutationOptions<ScriptComment, AxiosError, { body: string }>({
            mutationKey: ['script-comments', 'script-comments:create', scriptId],
            mutationFn: async (payload) => {
                const response = await api.post(`${ApiEndpoint.ScriptComments}/${scriptId}/comments`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['script-comments', scriptId] });
            },
            onError: (error) => {
                let message = 'Не вдалося додати коментар';
                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }
                useUIStore.getState().setNotification({ type: 'error', message });
            },
        }),

    update: (scriptId: string, commentId: string) =>
        mutationOptions<ScriptComment, AxiosError, { body: string }>({
            mutationKey: ['script-comments', 'script-comments:update', commentId],
            mutationFn: async (payload) => {
                const response = await api.put(`${ApiEndpoint.ScriptComments}/${scriptId}/comments/${commentId}`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['script-comments', scriptId] });
                queryClient.invalidateQueries({ queryKey: ['my-comments'] });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити коментар';
                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }
                useUIStore.getState().setNotification({ type: 'error', message });
            },
        }),

    delete: (scriptId: string, commentId: string) =>
        mutationOptions<void, AxiosError, void>({
            mutationKey: ['script-comments', 'script-comments:delete', commentId],
            mutationFn: async () => {
                await api.delete(`${ApiEndpoint.ScriptComments}/${scriptId}/comments/${commentId}`);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['script-comments', scriptId] });
                queryClient.invalidateQueries({ queryKey: ['my-comments'] });
            },
            onError: (error) => {
                let message = 'Не вдалося видалити коментар';
                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }
                useUIStore.getState().setNotification({ type: 'error', message });
            },
        }),

    markRead: (scriptId: string, commentId: string) =>
        mutationOptions<ScriptComment, AxiosError, void>({
            mutationKey: ['script-comments', 'script-comments:mark-read', commentId],
            mutationFn: async () => {
                const response = await api.post(`${ApiEndpoint.ScriptComments}/${scriptId}/comments/${commentId}/mark-read`);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['script-comments', scriptId] });
                queryClient.invalidateQueries({ queryKey: ['all-comments'] });
            },
            onError: (error) => {
                let message = 'Не вдалося позначити коментар як прочитаний';
                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }
                useUIStore.getState().setNotification({ type: 'error', message });
            },
        }),
};

export const settingsMutations = {
    create: () =>
        mutationOptions<Setting, AxiosError, SettingCreatePayload>({
            mutationKey: ['settings', 'settings:create'],
            mutationFn: async (payload: SettingCreatePayload) => {
                const response = await api.post(ApiEndpoint.Settings, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['settings'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Налаштування збережено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося зберегти налаштування';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),

    update: (key: string) =>
        mutationOptions<Setting, AxiosError, SettingUpdatePayload>({
            mutationKey: ['settings', 'settings:update', key],
            mutationFn: async (payload: SettingUpdatePayload) => {
                const response = await api.patch(`${ApiEndpoint.Settings}/${encodeURIComponent(key)}`, payload);
                return response.data.data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['settings'] });
                useUIStore.getState().setNotification({
                    type: 'success',
                    message: 'Налаштування оновлено',
                });
            },
            onError: (error) => {
                let message = 'Не вдалося оновити налаштування';

                if (error instanceof AxiosError && error.response?.data) {
                    const data = error.response.data as { message?: string };
                    message = data.message || message;
                }

                useUIStore.getState().setNotification({
                    type: 'error',
                    message,
                });
            },
        }),
};
